import { useState, useRef, useEffect } from 'react';
import { Upload, X, Image, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
const API = `${BACKEND_URL}/api`;

const getAuthHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
});

export default function ImageUploader({ 
  currentImage, 
  onImageChange, 
  label = "Immagine",
  className = "",
  isDark = true 
}) {
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(currentImage || '');
  const fileInputRef = useRef(null);

  useEffect(() => {
    setPreview(currentImage || '');
  }, [currentImage]);

  const prepareImageForUpload = (file) => new Promise((resolve) => {
    if (file.type === 'image/gif' || (file.size <= 1.5 * 1024 * 1024)) {
      resolve(file);
      return;
    }

    const objectUrl = URL.createObjectURL(file);
    const image = new window.Image();
    image.onload = () => {
      const maxDimension = 1600;
      const scale = Math.min(1, maxDimension / Math.max(image.width, image.height));
      const canvas = document.createElement('canvas');
      canvas.width = Math.max(1, Math.round(image.width * scale));
      canvas.height = Math.max(1, Math.round(image.height * scale));
      const context = canvas.getContext('2d');
      context.drawImage(image, 0, 0, canvas.width, canvas.height);
      URL.revokeObjectURL(objectUrl);

      canvas.toBlob((blob) => {
        if (!blob) {
          resolve(file);
          return;
        }
        const baseName = (file.name || 'immagine').replace(/\.[^.]+$/, '');
        resolve(new File([blob], `${baseName}.webp`, { type: 'image/webp' }));
      }, 'image/webp', 0.82);
    };
    image.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      resolve(file);
    };
    image.src = objectUrl;
  });

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Tipo file non supportato. Usa JPG, PNG, WebP o GIF');
      return;
    }

    // Le foto grandi vengono ridimensionate automaticamente prima del caricamento.
    if (file.size > 15 * 1024 * 1024) {
      toast.error('File troppo grande. Massimo 15MB prima dell’ottimizzazione');
      return;
    }

    // Show preview immediately
    const reader = new FileReader();
    reader.onload = (e) => setPreview(e.target.result);
    reader.readAsDataURL(file);

    // Upload to server
    setUploading(true);
    try {
      const uploadFile = await prepareImageForUpload(file);
      if (uploadFile.size > 5 * 1024 * 1024) {
        throw new Error('Immagine ancora troppo grande dopo l’ottimizzazione');
      }

      const formData = new FormData();
      formData.append('file', uploadFile);

      const response = await axios.post(`${API}/upload/image`, formData, getAuthHeaders());

      const imageUrl = response.data.url || response.data.image_data;
      if (!imageUrl) throw new Error('URL immagine non ricevuto');
      setPreview(imageUrl);
      onImageChange(imageUrl);
      toast.success('Immagine pronta. Premi Salva per confermare.');
    } catch (error) {
      toast.error(error.response?.data?.detail || error.message || 'Errore nel caricamento');
      setPreview(currentImage || '');
    } finally {
      setUploading(false);
    }
  };

  const handleRemove = () => {
    setPreview('');
    onImageChange('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const t = {
    card: isDark ? 'bg-white/5 border-white/10' : 'bg-gray-50 border-gray-200',
    text: isDark ? 'text-white' : 'text-gray-900',
    textSecondary: isDark ? 'text-white/60' : 'text-gray-600',
    textMuted: isDark ? 'text-white/40' : 'text-gray-400',
    hover: isDark ? 'hover:bg-white/10 hover:border-white/30' : 'hover:bg-gray-100 hover:border-gray-300',
  };

  return (
    <div className={className}>
      <label className={`block ${t.textSecondary} text-sm mb-2`}>{label}</label>
      
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/gif"
        onChange={handleFileSelect}
        className="hidden"
      />

      {preview ? (
        // Image Preview
        <div className="relative group">
          <div className={`relative rounded-xl overflow-hidden border ${t.card}`}>
            <img 
              src={preview} 
              alt="Preview" 
              className="w-full h-48 object-cover"
            />
            {uploading && (
              <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                <Loader2 className="w-8 h-8 text-white animate-spin" />
              </div>
            )}
          </div>
          
          {/* Action buttons */}
          <div className="absolute top-2 right-2 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="p-2 bg-white/90 text-gray-700 rounded-lg hover:bg-white transition-colors shadow-lg"
              title="Cambia immagine"
            >
              <Upload size={16} />
            </button>
            <button
              type="button"
              onClick={handleRemove}
              className="p-2 bg-red-500/90 text-white rounded-lg hover:bg-red-500 transition-colors shadow-lg"
              title="Rimuovi immagine"
            >
              <X size={16} />
            </button>
          </div>
        </div>
      ) : (
        // Upload Area
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
          className={`w-full h-48 rounded-xl border-2 border-dashed ${t.card} ${t.hover} transition-all flex flex-col items-center justify-center gap-3 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed`}
        >
          {uploading ? (
            <>
              <Loader2 className={`w-10 h-10 ${t.textMuted} animate-spin`} />
              <span className={t.textSecondary}>Caricamento...</span>
            </>
          ) : (
            <>
              <div className={`w-16 h-16 rounded-full ${isDark ? 'bg-white/10' : 'bg-gray-200'} flex items-center justify-center`}>
                <Image className={`w-8 h-8 ${t.textMuted}`} />
              </div>
              <div className="text-center">
                <p className={`${t.text} font-medium`}>Clicca per caricare</p>
                <p className={`${t.textMuted} text-sm`}>JPG, PNG, WebP o GIF · foto ottimizzate automaticamente</p>
              </div>
            </>
          )}
        </button>
      )}
    </div>
  );
}
